import { useEffect, useState, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Grid } from '@mui/material';
import baseAxios from 'axios';
import TitleDetailTitleBarV2 from './TitleDetailTitleBarV2';
import {
  fetchTitleV2,
  getTitleVersions,
  clearTitleSlice,
  setActiveTitleId,
  setRootTitleId,
  fetchTitleChildrenV2,
  clearCache,
  setActiveVersion
} from '@/redux/slices/titleV2Slice';
import {
  selectActiveVersionsByTitleIdMap,
  selectChildrenByTitleIdMap,
  selectIsRootTitleOrVersionsLoading,
  selectRootTitle,
  selectTitlesById
} from '@/redux/selectors/titleV2Selectors';
import { selectTmdbTitle } from '@/_utils/titleUtils';
import { tmdb_multi_search } from '@/constants/constants';
import TitleDetailTabsV2 from './TitleDetailTabsV2';
import TitleDetailHeaderV2 from './TitleDetailHeaderV2';
import { insertWordBreaks, checkIfUrlIsValid } from '@/_utils/utils';
import Poster from '@/components/_common/Poster';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useToast } from '@/components/_common/Toast';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { useTitleURLParser } from '@/components/TitleDetail/v2/hooks/useTitleURLParser';

type NestingLevel = number;

interface Abortable {
  abort(): void;
}

interface EntityRequestMap {
  title: Abortable | null;
  children: Abortable | null;
  versions: Abortable | null;
}

const TitleDetailV2 = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { path, rootTitleId, activeTitleId } = useTitleURLParser();
  const titlesById = useAppSelector(selectTitlesById);
  const childrenById = useAppSelector(selectChildrenByTitleIdMap);
  const versionsById = useAppSelector(selectActiveVersionsByTitleIdMap);
  const rootTitle = useAppSelector(selectRootTitle);
  const isRootTitleLoading = useAppSelector(selectIsRootTitleOrVersionsLoading);
  const disableTMDB = useContext(ConfigContext)?.titleConfig?.disableTMDB;
  const [isThumbnailLoading, setIsThumbnailLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const reloadScheduled = useRef(false);

  useEffect(() => {
    return () => {
      dispatch(clearTitleSlice());
    };
  }, []);

  useEffect(() => {
    dispatch(setRootTitleId(rootTitleId));
  }, [rootTitleId]);

  useEffect(() => {
    dispatch(setActiveTitleId(activeTitleId));
    dispatch(setActiveVersion(null));
  }, [activeTitleId]);

  // We store thunk action refs for every nesting level of titles, so we could independently abort loading of any instance on any nesting level.
  const requestsRef = useRef<EntityRequestMap[]>([]);
  const { error: errorToast } = useToast();

  const populateLevel = (level: NestingLevel) => {
    requestsRef.current[level] = {
      title: null,
      children: null,
      versions: null
    };
  };

  const loadTitleWithChildren = (titleId: number, level: NestingLevel, parentTitleId: number | undefined, onError?: (error: any) => void) => {
    if (!requestsRef.current[level]) populateLevel(level);
    requestsRef.current[level].title?.abort();

    const isTitleLoaded = Boolean(titlesById[titleId]);
    let titlePromise: Promise<TitleEntityV2 | void>;

    if (isTitleLoaded) {
      const titleHasNonVersionChildren = Boolean(titlesById[titleId].relationships.children.find((c) => c.entity_type !== 'version'));
      titlePromise = Promise.resolve(titlesById[titleId]);
      // If the title is already loaded and we know it doesn't have children, there is no reason to load them
      if (titleHasNonVersionChildren) {
        loadChildren(titleId, level);
      }
    } else {
      const asyncAction = dispatch(fetchTitleV2(titleId));
      requestsRef.current[level].title = asyncAction;
      titlePromise = asyncAction.unwrap().catch((error) => {
        if (onError) onError(error);
        if (error.name !== 'AbortError') errorToast(`Failed to load title for title ID ${titleId}`);
      });

      let shouldLoadChildren = true;
      if (parentTitleId !== undefined && childrenById[parentTitleId]) {
        const titleRelationshipChildren = childrenById[parentTitleId].find((c) => c.id === titleId)?.relationships.children;
        const hasNonVersionChildren = titleRelationshipChildren && titleRelationshipChildren.some((c) => c.entity_type !== 'version');
        shouldLoadChildren = Boolean(hasNonVersionChildren);
      }
      if (shouldLoadChildren) {
        loadChildren(titleId, level);
      }
    }

    titlePromise.then((title) => {
      if (
        title &&
        title.id === activeTitleId // Load versions only for active title
      ) {
        const hasVersions = Boolean(title.relationships.children.find((child) => child.entity_type === 'version'));
        if (hasVersions) loadVersions(title.id, level);
      }
    });
  };

  const loadChildren = (titleId: number, level: NestingLevel) => {
    requestsRef.current[level]?.children?.abort();

    const areChildrenLoaded = Boolean(childrenById[titleId]);
    // don't load children if they are already loaded
    if (areChildrenLoaded) return;

    const asyncAction = dispatch(fetchTitleChildrenV2(titleId));
    requestsRef.current[level].children = asyncAction;
    asyncAction.unwrap().catch((error) => {
      if (error.name !== 'AbortError') errorToast(`Failed to load children for title ID ${titleId}`);
    });
  };

  const loadVersions = (titleId: number, level: NestingLevel) => {
    requestsRef.current[level]?.versions?.abort();

    const areVersionsLoaded = Boolean(versionsById[titleId]);
    // don't load versions if they are already loaded
    if (areVersionsLoaded) return;

    const asyncAction = dispatch(getTitleVersions(titleId));
    requestsRef.current[level].versions = asyncAction;
    asyncAction.unwrap().catch((error) => {
      if (error.name !== 'AbortError') errorToast(`Failed to load versions for title ID ${titleId}`);
    });
  };

  const handleRootTitleLoadError = (error: any) => {
    if (error.name === 'AbortError') return;
    console.error(error);
    history.push('/page-not-found');
  };

  const loadHierarchy = () => {
    path.forEach((titleId, index) => {
      loadTitleWithChildren(
        titleId,
        index,
        path[index - 1],
        index === 0 ? handleRootTitleLoadError : undefined // pass error handler only to for the root title
      );
    });
  };

  useEffect(() => {
    loadHierarchy();
  }, [path]);

  const refreshData = () => {
    dispatch(clearCache());
    dispatch(setRootTitleId(rootTitleId));
    dispatch(setActiveTitleId(activeTitleId));
    reloadScheduled.current = true;
  };

  useEffect(() => {
    if (reloadScheduled.current) {
      reloadScheduled.current = false;
      loadHierarchy();
    }
  }, [reloadScheduled.current, loadHierarchy]);

  // Load title thumbnail
  useEffect(() => {
    const retrieveThumbnail = async () => {
      let workingUrl = null;

      for (const url of [rootTitle?.cover_art_url, rootTitle?.thumbnail_link]) {
        if (url) {
          const isValid = await checkIfUrlIsValid(url);
          if (isValid) {
            workingUrl = url;
            break;
          }
        }
      }

      if (workingUrl) {
        setThumbnail(workingUrl);
      } else {
        if (disableTMDB) {
          setThumbnail(null);
          setIsThumbnailLoading(false);
        } else if (rootTitle?.id) {
          fetchTmdb();
        }
      }
    };

    retrieveThumbnail();

    return () => {
      setThumbnail(null);
      setIsThumbnailLoading(false);
    };
  }, [rootTitle?.id]);

  const fetchTmdb = () => {
    if (!rootTitle) {
      console.error('title is undefined. This function should not be called if title is not loaded.');
      return;
    }

    const { lineage_meta, name, entity_type } = rootTitle;
    const year = rootTitle.meta?.characteristics?.release_year;
    setIsThumbnailLoading(true);
    baseAxios
      .get(tmdb_multi_search(lineage_meta?.series_name || name, entity_type, year))
      .then((res) => {
        // if we got no results searching including year, search without bc sometimes we have the wrong year
        if (!res.data?.results?.length && year) {
          return baseAxios.get(tmdb_multi_search(lineage_meta?.series_name || name, entity_type));
        } else return res;
      })
      .then((res) => {
        const selectedTitle = selectTmdbTitle(res.data?.results);
        if (selectedTitle) {
          setThumbnail(`https://image.tmdb.org/t/p/original/${selectedTitle.poster_path}`);
        }
      })
      .catch(() => console.error('could not fetch tmdb data'))
      .then(() => setIsThumbnailLoading(false));
  };

  return (
    <>
      <TitleDetailTitleBarV2 onRefreshButtonClick={refreshData} />

      <Container>
        <Grid container spacing={5}>
          {/* POSTER */}
          <Grid item xs={6} lg={3} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Poster
              loading={isThumbnailLoading || isRootTitleLoading}
              src={thumbnail ?? undefined}
              title={rootTitle ? insertWordBreaks(rootTitle.name) : null}
            />
          </Grid>

          <Grid item xs={12} lg={9}>
            <TitleDetailHeaderV2 />
            {/* METADATA/INVENTORY TABS */}
            <TitleDetailTabsV2 />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default TitleDetailV2;
